/**
 * @file useSectionSort.ts
 * @brief U5: drag an Intel / Points section by its heading, the way the
 *        phone's Home drags a section (web + Mac app).
 *
 * @details
 * Web Home has no drag (it follows the phone's order, DES 11-10-2026) and no
 * right-click items, so the only Home drag to match is the phone's
 * (Sortable.Grid in FRONTEND HomeScreen.tsx + HomeSortableSection.tsx):
 * - Lift: the section scales to 1.03 on a page-coloured backdrop with a soft
 *   shadow; the other sections dim to 0.55 and slide out of its way.
 * - Start: a mouse starts dragging after 5 px of movement (a click still
 *   folds); a finger holds 350 ms first, as on the phone, so a swipe scrolls.
 * - While dragging, EVERY section of the tab is folded to its heading, so a
 *   long section is as easy to move as a short one; on drop they open again
 *   as they were, with the dropped heading kept under the pointer.
 * - Escape cancels. Near the scroll area's top or bottom edge the page
 *   scrolls. Keyboard: Alt+↑ / Alt+↓ on a focused heading (IntelLayoutFrame).
 * The look is applied straight to the DOM while dragging (no React render per
 * pointer move: MeetingDetailView is large). Only the start (fold) and the end
 * (the new order, unfold) render. Geometry: ./sectionSort.ts.
 *
 * @author DES
 * @date 03-11-2026
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { planSort, rowsFromBoxes, type SortRow } from "./sectionSort";
import { unitIndexOf } from "./intelLayout";
import type { TabLayoutControl } from "./useIntelLayout";

/** Phone Home's numbers (HomeScreen Sortable.Grid). */
const LIFT_SCALE = 1.03;
const OTHERS_OPACITY = "0.55";
const HOLD_MS = 350;
const MOUSE_SLOP = 5;
const TOUCH_SLOP = 8;
const EDGE = 56;
const SLIDE = "transform 200ms cubic-bezier(0.2, 0, 0, 1), opacity 150ms ease";

export interface SectionSort {
  /** The unit being dragged (its first key), or null. */
  activeKey: string | null;
  /** pointerdown on a heading; `sectionKey` is that heading's key. */
  onPointerDown: (sectionKey: string, e: React.PointerEvent<HTMLElement>) => void;
}

/** The attributes a unit's outer element carries (key null = never moves). */
export function sortUnitAttrs(tab: string, key: string | null) {
  return { "data-sort-tab": tab, "data-sort-key": key ?? "" };
}

type Scroller = { el: HTMLElement | null; top: () => number; by: (dy: number) => void; edges: () => [number, number] };

function scrollerOf(node: HTMLElement): Scroller {
  let el: HTMLElement | null = node.parentElement;
  while (el && el !== document.body) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) break;
    el = el.parentElement;
  }
  if (!el || el === document.body) {
    return {
      el: null,
      top: () => window.scrollY,
      by: (dy) => window.scrollBy(0, dy),
      edges: () => [0, window.innerHeight],
    };
  }
  const box = el;
  return {
    el: box,
    top: () => box.scrollTop,
    by: (dy) => {
      box.scrollTop += dy;
    },
    edges: () => {
      const r = box.getBoundingClientRect();
      return [Math.max(r.top, 0), Math.min(r.bottom, window.innerHeight)];
    },
  };
}

interface Session {
  key: string;
  pointerId: number;
  pointerType: string;
  x0: number;
  y0: number;
  heading: HTMLElement;
  clientY: number;
  active: boolean;
  timer: number | null;
  scroller?: Scroller;
  units?: HTMLElement[];
  rows?: SortRow[];
  dragged?: number;
  grab?: number;
  targetKey?: string | null;
  finalTop?: number;
  raf?: number | null;
}

function clearLook(units: HTMLElement[] | undefined) {
  for (const el of units ?? []) {
    for (const p of ["transform", "transition", "opacity", "position", "z-index", "isolation", "background", "box-shadow", "border-radius"]) {
      el.style.removeProperty(p);
    }
    el.querySelector(":scope > [data-sort-backdrop]")?.remove();
  }
}

/**
 * The lifted look (HomeSortableSection): a page-coloured backdrop with a
 * 10 px margin and the lift shadow. A unit with a box of its own (Needs
 * attention, the Attached notes card) lifts as that box; a bare heading gets
 * a backdrop that hugs what is drawn, never the gap below it (the phone
 * insets its backdrop for the same reason).
 */
function lift(el: HTMLElement) {
  const dark = document.documentElement.classList.contains("dark");
  const look = {
    background: "hsl(var(--app-page))",
    borderRadius: "16px",
    boxShadow: `0 0 0 10px hsl(var(--app-page)), 0 8px 18px 10px rgba(0,0,0,${dark ? 0.5 : 0.18})`,
  };
  el.style.zIndex = "40";
  const cs = getComputedStyle(el);
  if (parseFloat(cs.borderTopWidth) > 0 || cs.backgroundColor !== "rgba(0, 0, 0, 0)") {
    Object.assign(el.style, look);
    return;
  }
  const box = el.getBoundingClientRect();
  const drawn = Array.from(el.children)
    .map((c) => c.getBoundingClientRect())
    .filter((r) => r.height > 0);
  if (!drawn.length) return;
  const top = Math.min(...drawn.map((r) => r.top));
  const bottom = Math.max(...drawn.map((r) => r.bottom));
  const backdrop = document.createElement("div");
  backdrop.dataset.sortBackdrop = "";
  Object.assign(backdrop.style, look, {
    position: "absolute",
    pointerEvents: "none",
    zIndex: "-1",
    left: "0",
    right: "0",
    top: `${top - box.top}px`,
    height: `${bottom - top}px`,
  });
  el.style.isolation = "isolate";
  el.prepend(backdrop);
}

/**
 * Wraps a tab's control: `isCollapsed` is true for every key while a drag is
 * on (fold all), and `sort` starts the drag. Pass the result to the headings
 * and use it for the tab's folding.
 */
export function useSectionSort(ctl: TabLayoutControl): TabLayoutControl {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const session = useRef<Session | null>(null);
  // After a drop / cancel. "order": the moved unit's new order has not drawn
  // yet (the lifted look stays, so the old order never flashes); "open": the
  // sections open again, then this unit's top is kept at `viewportTop`.
  const settle = useRef<{
    phase: "order" | "open";
    key: string;
    index: number;
    viewportTop: number;
    scroller: Scroller;
    units?: HTMLElement[];
  } | null>(null);
  const ctlRef = useRef(ctl);
  ctlRef.current = ctl;

  useLayoutEffect(() => {
    const s = settle.current;
    if (!s) return;
    if (s.phase === "order") {
      if (unitIndexOf(ctl.units, s.key) !== s.index) return;
      clearLook(s.units);
      s.phase = "open";
      setActiveKey(null);
      return;
    }
    if (activeKey) return;
    settle.current = null;
    const el = document.querySelector<HTMLElement>(
      `[data-sort-tab="${ctl.tab}"][data-sort-key="${CSS.escape(s.key)}"]`,
    );
    if (el) s.scroller.by(el.getBoundingClientRect().top - s.viewportTop);
  }, [ctl.units, ctl.tab, activeKey]);

  const end = useCallback(
    (commit: boolean) => {
      const s = session.current;
      session.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("touchmove", onTouchMove);
      if (!s) return;
      if (s.timer) window.clearTimeout(s.timer);
      if (s.raf) cancelAnimationFrame(s.raf);
      if (!s.active) return;
      document.body.style.removeProperty("user-select");
      document.body.style.removeProperty("cursor");
      // The click that ends a mouse drag must not fold the heading.
      const swallow = (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener("click", swallow, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener("click", swallow, true), 0);

      const c = ctlRef.current;
      const scroller = s.scroller as Scroller;
      const moved = commit && s.targetKey ? s.targetKey : null;
      // Where the heading is let go: its new slot, or back home.
      const home = s.rows?.[s.dragged ?? 0]?.top ?? 0;
      const viewportTop = (moved ? (s.finalTop ?? home) : home) - scroller.top();
      if (moved) {
        settle.current = { phase: "order", key: s.key, index: unitIndexOf(c.units, moved), viewportTop, scroller, units: s.units };
        c.drop(s.key, moved);
        // Never stay lifted if the new order somehow does not draw.
        window.setTimeout(() => {
          const st = settle.current;
          if (st?.phase !== "order" || st.key !== s.key) return;
          clearLook(st.units);
          st.phase = "open";
          setActiveKey(null);
        }, 600);
      } else {
        clearLook(s.units);
        settle.current = { phase: "open", key: s.key, index: -1, viewportTop, scroller };
        setActiveKey(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the listeners below are stable refs
    [],
  );

  const place = useCallback(() => {
    const s = session.current;
    if (!s?.active || !s.rows || !s.units || s.dragged === undefined || !s.scroller) return;
    const draggedTop = s.clientY + s.scroller.top() - (s.grab ?? 0);
    const plan = planSort(s.rows, s.key, draggedTop);
    s.targetKey = plan.targetKey;
    s.finalTop = plan.tops[s.dragged];
    s.units.forEach((el, i) => {
      if (i === s.dragged) {
        el.style.transform = `translateY(${draggedTop - s.rows![i].top}px) scale(${LIFT_SCALE})`;
      } else {
        el.style.transform = `translateY(${plan.tops[i] - s.rows![i].top}px)`;
      }
    });
  }, []);

  const autoScroll = useCallback(() => {
    const s = session.current;
    if (!s?.active || !s.scroller) return;
    const [top, bottom] = s.scroller.edges();
    const dy = s.clientY < top + EDGE ? -(top + EDGE - s.clientY) / 4 : s.clientY > bottom - EDGE ? (s.clientY - bottom + EDGE) / 4 : 0;
    if (dy) {
      s.scroller.by(Math.max(-14, Math.min(14, dy)));
      place();
    }
    s.raf = requestAnimationFrame(autoScroll);
  }, [place]);

  const activate = useCallback(() => {
    const s = session.current;
    if (!s || s.active) return;
    const unit = s.heading.closest<HTMLElement>("[data-sort-tab]");
    if (!unit) return end(false);
    s.active = true;
    s.timer = null;
    const scroller = scrollerOf(unit);
    s.scroller = scroller;
    const headingTop0 = s.heading.getBoundingClientRect().top;
    // Fold every section now, then keep the grabbed heading where it was.
    flushSync(() => setActiveKey(s.key));
    scroller.by(s.heading.getBoundingClientRect().top - headingTop0);

    // Drawn units only (the demo's hidden notes card has no box).
    const units = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-sort-tab="${ctlRef.current.tab}"]`),
    ).filter((el) => el.getClientRects().length > 0);
    const st = scroller.top();
    const boxes = units.map((el) => {
      const r = el.getBoundingClientRect();
      return { key: el.dataset.sortKey || null, top: r.top + st, height: r.height };
    });
    const last = units[units.length - 1];
    s.units = units;
    s.rows = rowsFromBoxes(boxes, last ? parseFloat(getComputedStyle(last).marginBottom) || 0 : 0);
    s.dragged = units.indexOf(unit);
    s.grab = s.clientY + st - boxes[s.dragged].top;

    units.forEach((el, i) => {
      el.style.position = "relative";
      el.style.transition = i === s.dragged ? "box-shadow 150ms ease" : SLIDE;
      if (i === s.dragged) lift(el);
      else el.style.opacity = OTHERS_OPACITY;
    });
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    place();
    s.raf = requestAnimationFrame(autoScroll);
  }, [autoScroll, end, place]);

  // Stable listeners (they read the session ref).
  const onMove = useRef((e: PointerEvent) => {
    const s = session.current;
    if (!s || e.pointerId !== s.pointerId) return;
    s.clientY = e.clientY;
    if (s.active) {
      e.preventDefault();
      place();
      return;
    }
    const dist = Math.hypot(e.clientX - s.x0, e.clientY - s.y0);
    if (s.pointerType === "touch") {
      if (dist > TOUCH_SLOP) end(false); // a swipe: let the page scroll
    } else if (dist > MOUSE_SLOP) {
      activate();
    }
  }).current;
  const onUp = useRef((e: PointerEvent) => {
    if (session.current && e.pointerId === session.current.pointerId) end(true);
  }).current;
  const onCancel = useRef(() => end(false)).current;
  const onKey = useRef((e: KeyboardEvent) => {
    if (e.key === "Escape" && session.current?.active) {
      e.preventDefault();
      e.stopPropagation();
      end(false);
    }
  }).current;
  // A held finger must not also scroll the page once it drags.
  const onTouchMove = useRef((e: TouchEvent) => {
    if (session.current?.active) e.preventDefault();
  }).current;

  useEffect(() => () => end(false), [end]);

  const onPointerDown = useCallback(
    (sectionKey: string, e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0 || !e.isPrimary || session.current) return;
      // Controls inside a heading row (Attach, Tips) keep their own click.
      const control = (e.target as HTMLElement).closest("button, a, input, textarea, select, [role='menuitem']");
      if (control && control !== e.currentTarget && e.currentTarget.contains(control)) return;
      const c = ctlRef.current;
      const unit = c.units.find((u) => u.includes(sectionKey));
      if (!unit || c.units.length < 2) return;
      session.current = {
        key: unit[0],
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        x0: e.clientX,
        y0: e.clientY,
        clientY: e.clientY,
        heading: e.currentTarget,
        active: false,
        timer: e.pointerType === "touch" ? window.setTimeout(activate, HOLD_MS) : null,
      };
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      window.addEventListener("keydown", onKey, true);
      window.addEventListener("touchmove", onTouchMove, { passive: false });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listeners are stable refs
    [activate],
  );

  return useMemo(
    () => ({
      ...ctl,
      isCollapsed: (key: string) => activeKey !== null || ctl.isCollapsed(key),
      sort: { activeKey, onPointerDown },
    }),
    [ctl, activeKey, onPointerDown],
  );
}
